// Color interpolation utilities for bubble teams
// Each team has its own color progression based on bubble size

// Color definitions for different teams
const TEAM_COLOR_MAPS = {
  default: {
    small: { r: 100, g: 200, b: 255 },    // Light blue
    medium: { r: 50, g: 150, b: 255 },    // Blue
    large: { r: 0, g: 100, b: 200 }       // Dark blue
  },

  team1: {
    small: { r: 150, g: 255, b: 150 },    // Light green
    medium: { r: 100, g: 255, b: 100 },   // Green
    large: { r: 50, g: 200, b: 50 }       // Dark green
  },

  team2: {
    small: { r: 255, g: 200, b: 150 },    // Light orange
    medium: { r: 255, g: 150, b: 100 },   // Orange
    large: { r: 200, g: 100, b: 50 }      // Dark orange
  },

  team3: {
    small: { r: 255, g: 150, b: 255 },    // Light purple
    medium: { r: 200, g: 100, b: 255 },   // Purple
    large: { r: 150, g: 50, b: 200 }      // Dark purple
  },

  team4: {
    small: { r: 255, g: 255, b: 150 },    // Light yellow
    medium: { r: 255, g: 200, b: 100 },   // Gold
    large: { r: 200, g: 150, b: 50 }      // Dark gold
  }
};

// Size thresholds for color transitions
const SIZE_THRESHOLDS = {
  smallToMedium: 200,
  mediumToLarge: 500
};

/**
 * Interpolates between two colors based on a ratio (0-1)
 * @param {Object} color1 - Starting color {r, g, b}
 * @param {Object} color2 - Ending color {r, g, b}
 * @param {number} t - Interpolation ratio (0-1)
 * @returns {Object} Interpolated color {r, g, b}
 */
const interpolateRGB = (color1, color2, t) => {
  return {
    r: Math.round(color1.r + (color2.r - color1.r) * t),
    g: Math.round(color1.g + (color2.g - color1.g) * t),
    b: Math.round(color1.b + (color2.b - color1.b) * t)
  };
};

/**
 * Gets the interpolated color for a bubble based on size and team
 * @param {number} bubbleSize - Current bubble size
 * @param {string} team - Team identifier ('default', 'team1', 'team2', etc.)
 * @returns {Object} Color object with background and boxShadow styles
 */
export const getBubbleColor = (bubbleSize, team = 'default') => {
  // Get the color map for this team (fallback to default)
  const colorMap = TEAM_COLOR_MAPS[team] || TEAM_COLOR_MAPS.default;

  let color;

  if (bubbleSize <= SIZE_THRESHOLDS.smallToMedium) {
    // Small to medium transition
    if (bubbleSize <= 100) {
      color = colorMap.small;
    } else {
      const t = (bubbleSize - 100) / (SIZE_THRESHOLDS.smallToMedium - 100);
      color = interpolateRGB(colorMap.small, colorMap.medium, t);
    }
  } else if (bubbleSize <= SIZE_THRESHOLDS.mediumToLarge) {
    // Medium size range
    color = colorMap.medium;
  } else if (bubbleSize <= 800) {
    // Medium to large transition
    const t = (bubbleSize - SIZE_THRESHOLDS.mediumToLarge) / (800 - SIZE_THRESHOLDS.mediumToLarge);
    color = interpolateRGB(colorMap.medium, colorMap.large, t);
  } else {
    // Large size
    color = colorMap.large;
  }

  // Create darker outer ring for gradient effect
  const outerColor = {
    r: Math.max(0, color.r - 40),
    g: Math.max(0, color.g - 40),
    b: Math.max(0, color.b - 40)
  };

  return {
    background: `radial-gradient(circle at 30% 30%,
      rgba(255, 255, 255, 0.8),
      rgba(${color.r}, ${color.g}, ${color.b}, 0.6),
      rgba(${outerColor.r}, ${outerColor.g}, ${outerColor.b}, 0.8))`,
    boxShadow: `0 0 20px rgba(${color.r}, ${color.g}, ${color.b}, 0.4),
      inset 0 0 10px rgba(255, 255, 255, 0.2)`
  };
};

/**
 * Gets available team identifiers
 * @returns {string[]} Array of team identifiers
 */
export const getAvailableTeams = () => {
  return Object.keys(TEAM_COLOR_MAPS);
};

/**
 * Adds a new team color map
 * @param {string} teamId - Team identifier
 * @param {Object} colorMap - Color map with small, medium, large colors
 */
export const addTeamColorMap = (teamId, colorMap) => {
  TEAM_COLOR_MAPS[teamId] = colorMap;
};
